<template>
  <section class="pb-6 mb-2">
    <v-loading-box
      v-if="isLoading"
      class="amendment-summary-loading"
    />
    <template v-else>
      <div class="position-absolute call-to-action-placement">
        <div class="d-flex flex-column">
          <v-loader
            v-if="canValidateAmendment"
            id="apply-amendment-summary-button"
            :next="openApplyAmendmentModal"
            :title="loaderTitle"
            icon="arrow-white-right"
            class="mt-2"
          />
        </div>
      </div>
      <div class="container pb-6 pb-md-0">
        <div class="row">
          <div class="col-2 my-auto">
            <v-icon
              icon="chevron-left"
              class="cursor-pointer"
              @click="() => $router.back()"
            />
          </div>
          <div class="col-8 my-auto">
            <div class="h1 text-center mb-0">
              Voici vos modifications
            </div>
          </div>
          <div class="col-12 mt-5">
            <div
              v-for="(group, index) in modifications"
              :key="'group '+ index"
              class="card shadow mb-2"
            >
              <div class="card-body px-4 pt-4 pb-2 d-flex flex-column flex-md-row">
                <div class="display-min-width">
                  <v-highlight
                    class="h5 mb-2"
                    :color="(titles[index] || {}).color"
                  >
                    {{ (titles[index] || {}).name }}
                  </v-highlight>
                </div>
                <div class="d-flex flex-column col">
                  <div
                    v-for="change in group"
                    :key="change.id"
                    class="d-flex flex-column col"
                  >
                    <div
                      v-for="changeValue in getFormattedValue(change)"
                      :key="changeValue.id"
                      class="fw-bold d-flex flex-row mb-2"
                    >
                      <div
                        class="d-flex"
                      >
                        <del v-if="changeValue.deleted">
                          {{ changeValue.name }}
                        </del>
                        <span v-else>
                          {{ changeValue.name }}
                        </span>
                      </div>
                      <div
                        v-if="visible"
                        class="d-flex ms-auto"
                      >
                        <v-loader-icon
                          :action="() => removeChange(change)"
                          class="ms-auto"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!hasModifications">
            <p>Vous n'avez apporté aucune modification.</p>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>
<script>
import { mapGetters } from 'vuex';
import { intersectionWith } from 'lodash-es';
import { ui } from '@/mixins';
import formatter from '@/utils/_Strings';
import LoaderIcon from '@/components/common/helpers/LoaderIcon.vue';
import LoadingAnimation from '@/components/common/helpers/LoadingAnimation.vue';

export default {
  name: 'AmendmentSummary',
  components: {
    'v-loader-icon': LoaderIcon,
    'v-loading-box': LoadingAnimation,
  },
  mixins: [ui],
  beforeRouteLeave(to, from, next) {
    const paths = [
      `/client/contracts/${this.$route.params.id}`,
      `/client/contracts/${this.$route.params.id}/declarations`,
    ];
    if (!paths.includes(to.path)) {
      this.ui_showMenu(true);
    }
    next();
  },
  data() {
    return {
      isLoading: true,
      visible: false,
      titles: {
        extensions: { name: 'Vos options (extensions)', color: 'yellow-300' },
        liabilities: { name: 'Votre garantie', color: 'pink-300' },
        deductibles: { name: 'Franchise', color: 'blue-300' },
        mainActivity: { name: 'Activité principale', color: 'yellow-300' },
        activities: { name: 'Activités complémentaires', color: 'blue-300' },
        reportedRevenue: { name: 'Chiffre d’affaires', color: 'pink-300' },
        effectif: { name: 'Effectifs', color: 'pink-300' },
        employeesBracket: { name: 'Nombre de salariés', color: 'green-300' },
        effectiveDate: { name: 'Date d\'efffet', color: 'blue-300' },
        pertefinanciere: { name: 'Option pertes financières', color: 'pink-300' },
        oriasStatus: { name: 'Status Orias', color: 'blue-300' },
        local: { name: 'Local', color: 'green-300' },
        fixContent: { name: 'Valeur du matériel fixe', color: 'pink-300' },
        mobileContent: { name: 'Valeur du matériel mobile', color: 'blue-300' },
        territorialite: { name: 'Territorialité', color: 'yellow-300' },
        oriasNumber: { name: 'Numéro ORIAS', color: 'blue-300' },
        accountantNumber: { name: 'Numéro d\'enregistrement au registre des Experts Comptables', color: 'green-300' },
        accountantRegistrationDate: { name: 'Date d\'enregistrement', color: 'pink-300' },
        auditorNumber: { name: 'Numéro d\'auditeur', color: 'yellow-300' },
        surveyorNumber: { name: 'Numéro de géomètre', color: 'blue-300' },
      },
    };
  },
  computed: {
    ...mapGetters({
      contract: 'contract/get',
      amendmentPricing: 'contract/amendmentPricing',
      amendmentModification: 'contract/amendmentModification',
      employeesBrackets: 'rater/employeesBrackets',
    }),
    modifications() {
      if (this.amendmentModification.length > 0) {
        return (this.amendmentModification || [])
          .filter(({ editable }) => editable)
          .reduce((acc, change) => {
            if (acc[change.group]) {
              acc[change.group].push(change);
            } else {
              acc[change.group] = [change];
            }
            return acc;
          }, {});
      }
      return [];
    },
    hasModifications() {
      return this.amendmentModification.length > 0;
    },
    isAmendmentValidated() {
      return this.contract.status === 'AMENDMENT' && this.contract.leadStatus === 'AMENDMENT_VALIDATED';
    },
    canValidateAmendment() {
      return this.hasModifications && !this.isAmendmentValidated;
    },
    loaderTitle() {
      if (!this.amendmentPricing.isReady || this.amendmentPricing.isZero) {
        return 'Valider l\'avenant';
      }
      return `Valider pour ${this.amendmentPricing.displayAmount} ${this.amendmentPricing.displaySuffix}`;
    },
  },
  async beforeMount() {
    this.isLoading = true;
    try {
      await this.$store.dispatch('contract/getContractDetail', {
        id: this.$route.params.id,
        entity: 'subscription',
      });
      await this.$store.dispatch('contract/findChanges', this.$route.params.id);
    } catch (error) {
      this.$router.replace('/failure');
    } finally {
      this.isLoading = false;
    }
  },
  mounted() {
    this.ui_showMenu(false);
  },
  methods: {
    parseJsonValue(value) {
      if (typeof value !== 'string') {
        return value;
      }

      try {
        return JSON.parse(value);
      } catch (error) {
        return value;
      }
    },
    formatNumber(string) {
      return parseFloat(string || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
    },
    getExtensionsIntersection({ newValue, oldValue }) {
      const old = this.parseJsonValue(oldValue) || [];
      const current = this.parseJsonValue(newValue) || [];

      const isExtensionDifferent = (a, b) => a.id === b.id && a.enabled !== b.enabled;
      // TODO verify this function is properly working
      const findExtension = ({ id }) => current.find(({ id: currentId }) => currentId === id);
      return intersectionWith(old, current, isExtensionDifferent)
        .map((elem) => {
          if (elem.enabled) {
            return Object.assign({}, elem, { deleted: true });
          }

          return Object.assign({}, findExtension(elem) || elem, { deleted: false });
        });
    },
    getFormattedLiabilities(newValue, id) {
      const parsedValue = this.parseJsonValue(newValue);
      const { liabilities: contractLiabilities = [] } = this.contract;
      let liabilities = [];
      if (Array.isArray(parsedValue)) {
        liabilities = parsedValue;
      } else if (Array.isArray(contractLiabilities)) {
        liabilities = contractLiabilities;
      }

      if (Array.isArray(parsedValue)) {
        const enabledLiability = parsedValue.find(({ enabled }) => enabled);
        if (enabledLiability) {
          return [enabledLiability];
        }
      }

      if (parsedValue && typeof parsedValue === 'object' && parsedValue.name) {
        return [parsedValue];
      }

      const liabilityId = typeof parsedValue === 'string'
        ? parsedValue
        : parsedValue && parsedValue.id;
      const resolvedLiability = liabilities.find(({ id: currentId, enabled }) => (
        (liabilityId && currentId === liabilityId) || enabled
      ));

      if (resolvedLiability) {
        return [resolvedLiability];
      }

      return [{ id, name: typeof parsedValue === 'string' ? parsedValue : '' }];
    },
    getFormattedValue(change) {
      const {
        newValue, id, group, fieldName,
      } = change;
      switch (group) {
        case 'extensions':
          return this.getExtensionsIntersection(change);
        case 'activities':
          return this.parseJsonValue(newValue) || [];
        case 'liabilities':
          return this.getFormattedLiabilities(newValue, id);
        case 'deductibles':
          return [(this.parseJsonValue(newValue) || [])
            .find(({ enabled }) => enabled)];
        case 'employeesBracket':
          return [{ name: `${this.getEmployeesBracket(newValue).min} à ${this.getEmployeesBracket(newValue).max}`, id }];
        case 'effectiveDate':
          return [{ name: newValue.split('-').reverse().join('/'), id }];
        case 'reportedRevenue':
          return [{ name: `${this.formatNumber(newValue)} €`, id }];
        case 'effectif':
          return [{ name: `${newValue} salarié(s)`, id }];
        case 'local':
          if (fieldName === 'Superficie') {
            return [{ name: `${this.formatNumber(newValue)} m²`, id }];
          }
          if (fieldName === 'Valeur des biens') {
            return [{ name: `${this.formatNumber(newValue)} €`, id }];
          }
          if (fieldName === 'Address') {
            return [{ name: `${formatter.addressFormatter({ address: this.parseJsonValue(newValue) })}`, id }];
          }
          break;
        case 'pertefinanciere':
          if (fieldName === 'Période d\'indemnisation') {
            return [{ name: `${newValue} mois d'indemnisation`, id }];
          }
          if (fieldName === 'Franchise de la perte financière') {
            return [{ name: `${newValue} jour(s) de franchise`, id }];
          }
          return [{ name: `Formule ${newValue}`, id }];
        default:
          break;
      }
      return [{ id, name: change.newValue }];
    },
    async removeChange(change) {
      const { path, oldValue } = change;
      let value = null;
      switch (change.group) {
        case 'extensions':
        case 'deductibles':
          value = this.parseJsonValue(oldValue);
          break;
        case 'liabilities':
          value = this.contract.liabilities
            .map((liability) => {
              const previousLiability = this.parseJsonValue(oldValue) || {};
              return Object.assign({}, liability, { enabled: previousLiability.id === liability.id });
            });
          break;
        default:
          value = oldValue;
          break;
      }
      await this.$store.dispatch('contract/updateSubscription', { value, path });
      await this.$store.dispatch('contract/findChanges', this.$route.params.id);
    },
    openApplyAmendmentModal() {
      this.ui_showModalAmendment('apply-amendment', true);
      return Promise.resolve();
    },
    getEmployeesBracket(value) {
      return this.employeesBrackets.find(({ code }) => code === value);
    },
  },
};
</script>
<style scoped>
.call-to-action-placement {
  z-index:20;
  bottom: 25px;
  right: 100px
}

.amendment-summary-loading {
  min-height: 50vh;
}

@media screen and (min-width: 576px) {
  .display-min-width {
    min-width: 300px;
  }
}
@media screen and (max-width: 576px) {
  .call-to-action-placement {
    z-index:20;
    bottom: 90px;
    right: 24px;
    left: 24px;
  }
}
</style>
